import { Badge } from '../../components/ui/Chip';

// ------------------------------------------------------------------
// ReportFindingsRollup — "what is wrong" and "what to do about it", as one
// composition (story 4-81; P06 §4).
//
// Backs BOTH `ReportContent.flaggedItems` and `ReportContent.actions`
// (story 4-80): the same approved, evidenced-finding source
// (`getApprovedFindingsForReport`) underlies both rollups, narrowed for
// actions to the items that carry a recommendation. One primitive with a
// `variant` rather than two near-identical components, because the two
// differ only in which fields they show, not in shape.
//
// a11y: `<section>` with a real heading (`headingLevel`, so a caller nests
// it correctly under the report's own heading level) and each item as a
// list item, not a table — "observation" and "recommendation" are prose,
// not a grid relationship. The severity `Badge` never carries the tone
// alone: its own text ("high", "critical", …) is read by a screen reader
// regardless of colour.
//
// COLOUR: `Badge` resolves its tone strictly from tokens.css's semantic
// vars — nothing here introduces a literal colour.

export type ReportFindingSeverity = 'low' | 'medium' | 'high' | 'critical' | string;

type BadgeSeverity = 'low' | 'medium' | 'high' | 'critical';

const SEVERITY_BADGE_TONE: Record<BadgeSeverity, 'neutral' | 'warning' | 'destructive'> = {
  low: 'neutral',
  medium: 'warning',
  high: 'destructive',
  critical: 'destructive',
};

export interface ReportFindingRollupItem {
  observationText: string;
  recommendationText?: string | null;
  severityTag?: ReportFindingSeverity | null;
  /** Shown only when the variant is `flagged` (photos/notes linked as evidence). */
  evidenceCount?: number;
}

// Story 4-117 — "group findings by section/severity/status/assignee".
// `groupBy` reaching this component is ALWAYS `'severity'` in practice
// (`report.service.js#resolveFindingsGroupBy` downgrades `'section'`/
// `'status'`/`'assignee'` — no real data behind any of them, see that
// resolver's own comment — before this component ever sees them), but the
// dispatch below handles `'severity'` and falls through to the pre-4-117
// flat list for anything else, so a future story that adds real
// section/status/assignee data only has to add a branch here.
export type ReportFindingsGroupBy = 'severity' | 'section' | 'status' | 'assignee';

const SEVERITY_GROUP_ORDER: Array<{ key: BadgeSeverity; label: string }> = [
  { key: 'critical', label: 'Critical' },
  { key: 'high', label: 'High' },
  { key: 'medium', label: 'Medium' },
  { key: 'low', label: 'Low' },
];

/** `pdfExport.service.js#groupFindingsBySeverity` — same clusters, same
 * order, same "no group emitted when empty" rule. */
function groupFindingsBySeverity(
  items: ReportFindingRollupItem[],
): Array<{ key: string; label: string; items: ReportFindingRollupItem[] }> {
  const groups: Array<{ key: string; label: string; items: ReportFindingRollupItem[] }> = SEVERITY_GROUP_ORDER.map(
    ({ key, label }) => ({
      key,
      label,
      items: items.filter((item) => item.severityTag === key),
    }),
  ).filter((group) => group.items.length > 0);
  const unspecified = items.filter(
    (item) => !item.severityTag || !SEVERITY_GROUP_ORDER.some((g) => g.key === item.severityTag),
  );
  if (unspecified.length > 0) groups.push({ key: 'unspecified', label: 'Unspecified', items: unspecified });
  return groups;
}

export interface ReportFindingsRollupProps {
  variant: 'flagged' | 'actions';
  title: string;
  total: number;
  bySeverity?: { low: number; medium: number; high: number; critical: number };
  items: ReportFindingRollupItem[];
  headingLevel?: 2 | 3 | 4;
  className?: string;
  /** Story 4-85 — `ReportStyle.surfaceRadius` threaded to the severity `Badge`s. `rounded` (default) unchanged. */
  radius?: 'rounded' | 'sharp';
  /** Story 4-117 — default `'severity'`, byte-identical to every pre-4-117
   *  caller (which is what a flat, ungrouped list already amounted to). */
  groupBy?: ReportFindingsGroupBy;
  /**
   * Story 4-120 — `ReportStyle.borderDensity`. `'standard'` (default) is the
   * pre-4-120 hairline `border-border/60` unchanged; `'bold'` thickens the
   * SAME item-card border to `border-2`.
   */
  borderWeight?: 'standard' | 'bold';
  /**
   * Story 4-120 — `ReportStyle.variants.finding`. `'standard'` (default)
   * unchanged; `'compact'` tightens the item card's own padding/type scale —
   * independent of `SectionPresentation.density` (which governs ordinary
   * list/table sections, not this rollup).
   */
  compact?: boolean;
  /**
   * Story 4-120 — `ReportStyle.colorRoles.findingSeverity: 'brand'` resolved
   * color (already contrast-safe-derived at the call site, `ReportRender
   * .tsx`'s `resolveColorRoles`/`deriveSecondaryTextColor` — this component
   * never derives a color itself, only paints one it is handed). `null`
   * (default) keeps the existing 4-tone `SEVERITY_BADGE_TONE` palette
   * unchanged. When set, EVERY severity badge uses this ONE brand-derived
   * color for text/border (`Badge`'s own `color` override), with `critical`/
   * `high` rendered `weight="bold"` and `medium`/`low` `weight="subtle"` as
   * the one remaining visual distinction beyond the label text itself — a
   * real, contrast-safe, single-hue alternative to the standard 4-color
   * ramp, not a second 4-shade palette (documented scope-down, matching this
   * story's own "implement what's real, disable/degrade the rest honestly"
   * bar).
   */
  severityColor?: string | null;
}

const BOLD_SEVERITIES = new Set(['critical', 'high']);

function severityBadgeProps(
  severity: BadgeSeverity,
  radius: 'rounded' | 'sharp',
  severityColor?: string | null,
): { tone: 'neutral' | 'warning' | 'destructive'; weight: 'subtle' | 'bold'; radius: 'rounded' | 'sharp'; color?: string } {
  if (severityColor) {
    return { tone: 'neutral', weight: BOLD_SEVERITIES.has(severity) ? 'bold' : 'subtle', radius, color: severityColor };
  }
  return { tone: SEVERITY_BADGE_TONE[severity], weight: 'subtle', radius };
}

function FindingItem({
  item,
  variant,
  radius,
  borderWeight = 'standard',
  compact = false,
  severityColor = null,
}: {
  item: ReportFindingRollupItem;
  variant: 'flagged' | 'actions';
  radius: 'rounded' | 'sharp';
  borderWeight?: 'standard' | 'bold';
  compact?: boolean;
  severityColor?: string | null;
}): JSX.Element {
  return (
    <li
      className={`rounded-lg ${borderWeight === 'bold' ? 'border-2' : 'border'} border-border/60 ${
        compact ? 'p-2' : 'p-3'
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className={compact ? 'text-xs text-foreground' : 'text-sm text-foreground'}>{item.observationText}</p>
        {item.severityTag && item.severityTag in SEVERITY_BADGE_TONE && (
          <Badge {...severityBadgeProps(item.severityTag as BadgeSeverity, radius, severityColor)}>
            {item.severityTag}
          </Badge>
        )}
      </div>
      {item.recommendationText && (
        <p className={`mt-1 text-muted-foreground ${compact ? 'text-xs' : 'text-sm'}`}>
          <span className="font-semibold text-foreground">Recommendation: </span>
          {item.recommendationText}
        </p>
      )}
      {variant === 'flagged' && item.evidenceCount != null && item.evidenceCount > 0 && (
        <p className="mt-1 text-xs text-muted-foreground">
          {item.evidenceCount === 1 ? '1 piece of evidence' : `${item.evidenceCount} pieces of evidence`}
        </p>
      )}
    </li>
  );
}

export function ReportFindingsRollup({
  variant,
  title,
  total,
  bySeverity,
  items,
  headingLevel = 3,
  className = '',
  radius = 'rounded',
  groupBy = 'severity',
  borderWeight = 'standard',
  compact = false,
  severityColor = null,
}: ReportFindingsRollupProps): JSX.Element {
  const Heading = `h${headingLevel}` as 'h2' | 'h3' | 'h4';
  const itemProps = { variant, radius, borderWeight, compact, severityColor };
  return (
    <section className={`space-y-3 ${className}`} aria-label={title}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Heading className="text-sm font-bold text-foreground">{`${title} (${total})`}</Heading>
        {bySeverity && (
          <div className="flex flex-wrap gap-1.5">
            {(['critical', 'high', 'medium', 'low'] as const)
              .filter((key) => bySeverity[key] > 0)
              .map((key) => (
                <Badge key={key} {...severityBadgeProps(key, radius, severityColor)}>{`${bySeverity[key]} ${key}`}</Badge>
              ))}
          </div>
        )}
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {variant === 'flagged' ? 'Nothing flagged.' : 'No recommended actions.'}
        </p>
      ) : groupBy === 'severity' ? (
        <div className="space-y-3">
          {groupFindingsBySeverity(items).map((group) => (
            <div key={group.key} className="space-y-2">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {`${group.label} (${group.items.length})`}
              </h4>
              <ul className="space-y-2">
                {group.items.map((item, index) => (
                  <FindingItem key={`${item.observationText}-${index}`} item={item} {...itemProps} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((item, index) => (
            <FindingItem key={`${item.observationText}-${index}`} item={item} {...itemProps} />
          ))}
        </ul>
      )}
    </section>
  );
}
